import { useThree, useFrame } from "@react-three/fiber";
import { useEffect, useMemo } from "react";
import { EffectComposer } from "three/addons/postprocessing/EffectComposer.js";
import { RenderPass } from "three/addons/postprocessing/RenderPass.js";
import { ShaderPass } from "three/addons/postprocessing/ShaderPass.js";
import { Vector2 } from "three";

const PixelationShader = {
  uniforms: {
    tDiffuse: { value: null },
    resolution: { value: new Vector2(1, 1) },
    pixelSize: { value: 6 },
    normalEdgeStrength: { value: 0.3 },
    depthEdgeStrength: { value: 0.4 },
  },
  vertexShader: /* glsl */`
    varying vec2 vUv;

    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
    }
  `,
  fragmentShader: /* glsl */`
    uniform sampler2D tDiffuse;
    uniform vec2 resolution;
    uniform float pixelSize;
    uniform float normalEdgeStrength;
    uniform float depthEdgeStrength;
    varying vec2 vUv;

    float luma( vec3 color ) {
      return dot( color, vec3( 0.299, 0.587, 0.114 ) );
    }

    void main() {
      float size = max( pixelSize, 1.0 );
      vec2 cell = vec2( size ) / resolution;
      vec2 snappedUv = floor( vUv / cell ) * cell + cell * 0.5;

      vec4 baseColor = texture2D( tDiffuse, snappedUv );

      vec2 texel = vec2( 1.0 ) / resolution;
      vec4 sampleRight = texture2D( tDiffuse, snappedUv + vec2( texel.x, 0.0 ) );
      vec4 sampleLeft = texture2D( tDiffuse, snappedUv - vec2( texel.x, 0.0 ) );
      vec4 sampleUp = texture2D( tDiffuse, snappedUv + vec2( 0.0, texel.y ) );
      vec4 sampleDown = texture2D( tDiffuse, snappedUv - vec2( 0.0, texel.y ) );

  // Edge weights approximate the WebGPU example using only color data.
      float edgeLuma = 0.0;
  edgeLuma += abs( luma( baseColor.rgb ) - luma( sampleRight.rgb ) );
  edgeLuma += abs( luma( baseColor.rgb ) - luma( sampleLeft.rgb ) );
  edgeLuma += abs( luma( baseColor.rgb ) - luma( sampleUp.rgb ) );
  edgeLuma += abs( luma( baseColor.rgb ) - luma( sampleDown.rgb ) );
      edgeLuma = smoothstep( 0.05, 0.6, edgeLuma );

      float edgeNormal = 0.0;
      edgeNormal += length( baseColor.rgb - sampleRight.rgb );
      edgeNormal += length( baseColor.rgb - sampleLeft.rgb );
      edgeNormal += length( baseColor.rgb - sampleUp.rgb );
      edgeNormal += length( baseColor.rgb - sampleDown.rgb );
      edgeNormal = smoothstep( 0.1, 1.0, edgeNormal );

      float edgeFactor = 1.0;
      edgeFactor -= depthEdgeStrength * edgeLuma;
      edgeFactor -= normalEdgeStrength * edgeNormal;
      edgeFactor = clamp( edgeFactor, 0.0, 1.0 );

      gl_FragColor = vec4( baseColor.rgb * edgeFactor, baseColor.a );
    }
  `,
};

export function PixelationEffect({ pixelSize, normalEdgeStrength, depthEdgeStrength }) {
  const { gl, scene, camera, size } = useThree();

  const { composer, pixelPass } = useMemo(() => {
    const composer = new EffectComposer(gl);
    composer.renderToScreen = true;

    const renderPass = new RenderPass(scene, camera);
    const pixelPass = new ShaderPass(PixelationShader);

    composer.addPass(renderPass);
    composer.addPass(pixelPass);

    return { composer, pixelPass };
  }, [gl, scene, camera]);

  useEffect(() => {
    const pixelRatio = gl.getPixelRatio();
    composer.setPixelRatio(pixelRatio);
    composer.setSize(size.width, size.height);
    pixelPass.material.uniforms.resolution.value.set(size.width * pixelRatio, size.height * pixelRatio);
  }, [composer, pixelPass, size, gl]);

  useEffect(() => {
    pixelPass.material.uniforms.pixelSize.value = pixelSize;
  }, [pixelPass, pixelSize]);

  useEffect(() => {
    pixelPass.material.uniforms.normalEdgeStrength.value = normalEdgeStrength;
  }, [pixelPass, normalEdgeStrength]);

  useEffect(() => {
    pixelPass.material.uniforms.depthEdgeStrength.value = depthEdgeStrength;
  }, [pixelPass, depthEdgeStrength]);

  useEffect(() => {
    const originalAutoClear = gl.autoClear;
    gl.autoClear = false;
    return () => {
      gl.autoClear = originalAutoClear;
    };
  }, [gl]);

  useEffect(() => {
    return () => {
      composer.dispose();
    };
  }, [composer]);

  useFrame((_, delta) => {
    composer.render(delta);
  }, 1);

  return null;
}

export default PixelationEffect;
